import { ProgressSelectors, useTranslation } from '@web_opxp/features';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import { MOOD_ID, MOOD_TRACKER_LIMIT } from '../../base/constants';
import { printDayMonth } from '../../base/dateUtils';
import { IDomainProgress } from '../types';
import useUserIdFromRoute from '../useUserIdFromRoute';
import './MoodProgress.scss';
import MoodProgressBar from './MoodProgressBar';

export const MoodProgress: FC = () => {
  const { language } = useTranslation();
  const userId = useUserIdFromRoute();
  const moodResults = useSelector((s: ProgressSelectors.IAppState) => {
    const progressData = ProgressSelectors.getWorkoutProgressHistory(s, MOOD_ID, userId);
    return convertToDomainProgress(progressData);
  });
  // pad results with empty values
  const results: IDomainProgress[] = [
    ...Array(MOOD_TRACKER_LIMIT - 1 - moodResults.length).fill({ normalizedScore: 0, time: 0 }),
    ...moodResults,
  ].slice(0, MOOD_TRACKER_LIMIT - 1);

  if (!language) {
    return null;
  }

  return (
    <div className="mood-progress">
      <svg viewBox="0 0 453 164" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g transform="translate(40,9)">
          <circle cx="11.5" cy="11.5" r="11.5" fill="#357D45" />
          <path
            d="M18.5979 13.0617C18.5979 14.9444 17.85 16.75 16.5187 18.0812C15.1875 19.4125 13.3819 20.1604 11.4992 20.1604C9.61645 20.1604 7.81085 19.4125 6.47957 18.0812C5.14829 16.75 4.40039 14.9444 4.40039 13.0617L11.4992 13.0617H18.5979Z"
            fill="#333333"
          />
          <circle cx="7.09271" cy="8.24115" r="2.10833" fill="#333333" />
          <circle cx="15.9072" cy="8.24115" r="2.10833" fill="#333333" />
        </g>
        <g transform="translate(40,37)">
          <circle cx="11.5" cy="11.5" r="11.5" fill="#6FCF97" />
          <path
            d="M18.4777 14.3626C18.1743 15.9901 17.3116 17.4604 16.0388 18.5191C14.766 19.5779 13.1632 20.1584 11.5077 20.1604C9.85212 20.1624 8.24793 19.5857 6.97261 18.53C5.69729 17.4743 4.83106 16.0061 4.52376 14.3793L5.2213 14.2476C5.49787 15.7117 6.27748 17.0331 7.42527 17.9832C8.57305 18.9333 10.0168 19.4523 11.5068 19.4505C12.9968 19.4488 14.4393 18.9262 15.5848 17.9734C16.7303 17.0205 17.5068 15.6973 17.7798 14.2325L18.4777 14.3626Z"
            fill="#333333"
          />
          <circle cx="7.114" cy="9.41283" r="2.12963" fill="#333333" />
          <circle cx="15.9148" cy="9.41283" r="2.12963" fill="#333333" />
        </g>
        <g transform="translate(40,65)">
          <circle cx="11.5" cy="11.5" r="11.5" fill="#FFC53A" />
          <circle cx="7.09838" cy="9.37084" r="2.12963" fill="#333333" />
          <circle cx="15.9011" cy="9.37084" r="2.12963" fill="#333333" />
          <rect x="5.36719" y="14.9502" width="12.65" height="0.383333" fill="#333333" />
        </g>
        <g transform="translate(40,93)">
          <circle cx="11.5" cy="11.5" r="11.5" fill="#FF8C00" />
          <path
            d="M5.68851 15.1005C6.38203 14.4793 7.28316 13.9781 8.31809 13.638C9.35301 13.2979 10.4923 13.1286 11.6426 13.1438C12.7929 13.1591 13.9215 13.3585 14.9359 13.7258C15.9503 14.0931 16.8218 14.6178 17.4788 15.2569L16.8809 15.5383C16.2896 14.9631 15.5053 14.4909 14.5923 14.1604C13.6793 13.8298 12.6636 13.6503 11.6283 13.6366C10.593 13.6228 9.5677 13.7753 8.63627 14.0814C7.70483 14.3875 6.89382 14.8385 6.26965 15.3976L5.68851 15.1005Z"
            fill="#333333"
          />
          <ellipse cx="7.11205" cy="7.86904" rx="2.12963" ry="2.02822" fill="#333333" />
          <ellipse cx="15.9148" cy="7.86904" rx="2.12963" ry="2.02822" fill="#333333" />
        </g>
        <g transform="translate(40,121)">
          <ellipse cx="11.5" cy="11.474" rx="11.5" ry="11.474" fill="#FF4B55" />
          <path
            d="M4.34678 17.9098C4.34678 17.2414 4.54101 16.5795 4.91839 15.9619C5.29576 15.3443 5.84889 14.7832 6.54618 14.3105C7.24348 13.8379 8.07129 13.4629 8.98235 13.2071C9.89341 12.9513 10.8699 12.8197 11.856 12.8197C12.8421 12.8197 13.8186 12.9513 14.7297 13.2071C15.6407 13.4629 16.4685 13.8379 17.1658 14.3105C17.8631 14.7832 18.4163 15.3444 18.7936 15.9619C19.171 16.5795 19.3652 17.2414 19.3652 17.9098H18.6143C18.6143 17.3082 18.4395 16.7125 18.0999 16.1567C17.7602 15.6009 17.2624 15.0959 16.6349 14.6705C16.0073 14.2451 15.2623 13.9076 14.4423 13.6774C13.6223 13.4472 12.7435 13.3287 11.856 13.3287C10.9685 13.3287 10.0897 13.4472 9.26972 13.6774C8.44976 13.9076 7.70473 14.2451 7.07716 14.6705C6.4496 15.0959 5.95179 15.6009 5.61215 16.1567C5.27251 16.7125 5.0977 17.3082 5.0977 17.9098H4.34678Z"
            fill="#333333"
          />
          <ellipse cx="6.93787" cy="8.12792" rx="2.19568" ry="2.09472" fill="#333333" />
          <ellipse cx="16.016" cy="8.12792" rx="2.19568" ry="2.09472" fill="#333333" />
        </g>
        <g transform="translate(68,8)">
          <rect x="0" y="0" width="1" height="136" className="border" />
          {results.map((progress, index) => (
            <g key={index} transform={`translate(${index * 20})`}>
              <MoodProgressBar
                label={progress.time ? printDayMonth(progress.time, language) : ''}
                value={progress.normalizedScore}
                isMax={false}
                showDate={index % 3 === 0}
              />
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
};

// narrow typing of progress data needed for mood progress representation
type TCycleProgressEntry = { cycleId: number; result: number; timestamp: number };

function convertToDomainProgress(progress?: TCycleProgressEntry[] | null): IDomainProgress[] {
  if (!progress) {
    return [];
  }
  return progress
    .slice(1 - MOOD_TRACKER_LIMIT)
    .map(({ cycleId, result, timestamp }) => ({ cycleId, normalizedScore: result, time: timestamp }));
}

export default MoodProgress;
